import React, { useMemo } from 'react'

import classNames from 'clsx'
import { useConnect } from 'redux-bundler-hook'

import { Box } from '@mui/material'
import { makeStyles } from '@mui/styles'

import { inflect } from 'inflection'

import createLogger from '~/src/Lib/Logging'
import {
  DATE_FORMATS,
  formattedDate,
  getDateDiff,
  getDateTime,
} from '~/src/Lib/Utils'
import EntityThemeProvider from '~/src/UI/Shared/EntityThemeProvider'
import T from '~/src/UI/Shared/Typography'

import OverflowTooltip from '../OverflowTooltip'
import { percentageOfPhaseElapsed } from './utils'

const displayName = 'Harvest/Timeline'
const logger = createLogger(displayName)

const useStyles = makeStyles(theme => ({
  root: {
    background: theme.palette.black.inactive,
    padding: theme.spacing(1.5, 4, 3, 1.5),
  },
  heading: {
    padding: '4px 0',
    display: 'flex',
    justifyContent: 'space-between',
  },
  phaseGrid: {
    display: 'grid',
    gap: '4px',
  },
  phaseCell: {
    overflow: 'hidden',
  },
  phaseCellCurrent: {
    // Allow the "today dot" to overflow beyond its grid cell
    margin: theme.spacing(0, -2),
    padding: theme.spacing(0, 2),
  },
  phaseBar: {
    margin: theme.spacing(1, 0),
    width: '100%',
    height: '8px',
    borderRadius: '3px',
  },
  todayDot: {
    position: 'relative',
    top: '-3px',
    width: '14px',
    height: '14px',
    backgroundColor: 'white',
    borderRadius: '50%',
  },
}), { name: displayName })

const PhaseCell = ({ classes, isCurrent, phase }) => {
  const dateLabel = [
    phase.startDate && formattedDate(phase.startDate, DATE_FORMATS.TINY_DATE),
    `${phase.totalDays} ${inflect('days', phase.totalDays)}`,
  ].filter(Boolean).join(' - ')
  const left = isCurrent && `calc(${percentageOfPhaseElapsed(phase)} - 7px)`
  return (
    <EntityThemeProvider colorKey={phase.phaseType}>
      <div className={classNames(classes.phaseCell, isCurrent && classes.phaseCellCurrent)}>
        <Box
          className={classes.phaseBar}
          sx={theme => ({ backgroundColor: theme.palette.primary.light })}
        >
          {isCurrent ? (
            <div className={classes.todayDot} style={{ left }} />
          ) : null}
        </Box>
        <OverflowTooltip>
          <T.Bold color="primary" noWrap>{phase.name}</T.Bold>
          <T.Tiny color="primary" noWrap>{dateLabel}</T.Tiny>
        </OverflowTooltip>
      </div>
    </EntityThemeProvider>
  )
}

const HarvestTimeline = ({
  currentPhase: currentPhaseProp = null,
  harvest,
  harvestPhases,
  heading: propsHeading = null,
}) => {
  const {
    phases,
    rooms,
  } = useConnect(
    'selectPhases',
    'selectRooms',
  )
  const currentPhase = currentPhaseProp ?? phases[harvest.currentPhase]
  const roomId = currentPhase?.room
  const currentRoomName = rooms[roomId]?.name

  const classes = useStyles({ currentPhase, phases: harvestPhases })

  const heading = useMemo(() => {
    const today = getDateTime('now').startOf('day')
    const startDate = getDateTime(harvest.startDate)
    const endDate = getDateTime(harvest.endDate)

    const harvestDateDiff = endDate.diff(startDate)
    const totalDays = Math.round(harvestDateDiff.as('days'))
    const totalWeeks = Math.ceil(harvestDateDiff.as('weeks'))

    const planned = today < startDate
    const finished = today > endDate

    let leftSide
    if (planned) {
      leftSide = <T.Bold color="textPrimary">Planned</T.Bold>
    }
    if (finished) {
      leftSide = <T.Bold color="textPrimary">Finished</T.Bold>
    }
    if (currentPhase) {
      const dayOfPhase = getDateDiff(getDateTime(currentPhase.startDate)).daysDiff + 1
      const dayOfHarvest = getDateDiff(startDate).daysDiff + 1
      const weekOfHarvest = Math.ceil(dayOfHarvest / 7)
      leftSide = (
        <Box display="flex" gap={1.5}>
          <T.Bold color="textPrimary">
            Day {dayOfPhase} in {currentPhase.name} ({currentRoomName})
          </T.Bold>
          <T>Week {weekOfHarvest}</T>
        </Box>
      )
    }

    const rightSide = (
      <Box display="flex" gap={1.5}>
        <T.Bold color="textPrimary">{totalDays}d</T.Bold>
        <T>{totalWeeks} weeks</T>
      </Box>
    )

    return (
      <div className={classes.heading}>
        {leftSide}{rightSide}
      </div>
    )
  }, [classes.heading, currentPhase, currentRoomName, harvest.endDate, harvest.startDate])

  return (
    <div className={classes.root}>
      {propsHeading ?? heading}
      <Box className={classes.phaseGrid} gridTemplateColumns={harvestPhases.map(p => `minmax(4rem, ${p.totalDays}fr)`).join(' ')}>
        {harvestPhases.map(phase => (
          <PhaseCell
            key={phase.id}
            classes={classes}
            isCurrent={phase.id === harvest.currentPhase}
            phase={phase}
          />
        ))}
      </Box>
    </div>
  )
}

const Memoized = React.memo(HarvestTimeline)

export default Memoized
